import { TcProps } from 'src/components/TagCommander'
import {
  sharedTagCommanderVariables,
  TcFormNames,
  TcPageTemplateNames,
  tcRecipeCategoryFilterValues,
  tcRegimeFilterValues,
} from 'src/configuration/tagcommander'
import { MomentValues, RegimeValues } from 'src/types'

import {
  Objectives,
  Preferences,
  RecipeCategory,
} from '../../types/fam-graphql'
import * as config from '../../configuration'
import {
  COURSE_COMPOSITION_STEP,
  COURSE_RANGE_STEP,
  DIET_PRACTICE_STEP,
  DRIED_VEGETABLES_FREQUENCY_STEP,
  EXPRESS_STEP,
  FISH_FREQUENCY_STEP,
  FRUITS_VEGETABLES_QUANTITY_STEP,
  MEAT_FREQUENCY_STEP,
  PrettyOptionsObjectType,
  STARCHY_FOODS_FREQUENCY_STEP,
} from '../NutritionalValues'

export type TcFormData = {
  formName: TcFormNames
  hasStep: boolean
  stepNumber: number
  stepName: string
  isConfirmation: boolean
}

export type TagCommanderTransformerArgsType = {
  pageName: TcProps['datalayer']['page_name']
  tcTemplate: TcPageTemplateNames
  tcCAt1?: TcProps['datalayer']['page_cat1']
  tcCAt2?: TcProps['datalayer']['page_cat2']
  tcCAt3?: TcProps['datalayer']['page_cat3']
  tcCAt4?: TcProps['datalayer']['page_cat4']
  filters?: {
    [key: string]: any
  }
  searchQuery?: string
  isFormInPage?: boolean
  formData?: TcFormData | null
  triggerTcUpdate?: number
  extraData?: {
    [key: string]: string | number | boolean
  }
} & {
  userObjectives?: Objectives | null
  t?: (key: string) => string
} & {
  userPreferences?: Preferences | null
  t?: (key: string) => string
}

export function tagCommanderTransformer(args: TagCommanderTransformerArgsType) {
  const {
    pageName,
    tcTemplate,
    tcCAt1,
    tcCAt2,
    tcCAt3,
    tcCAt4,
    filters,
    searchQuery,
    userObjectives,
    t,
    formData,
    triggerTcUpdate,
    userPreferences,
    extraData,
  } = args

  function findObjectiveOptionText(
    func: (
      t: any,
      tips: null,
      getOptions: boolean
    ) => PrettyOptionsObjectType | any,
    value?: string | number | null
  ) {
    return func(t, null, true)?.[value || '']?.label
  }

  function findPreferenceOptionText(
    func: (
      t: any,
      tips: null,
      getOptions: boolean
    ) => PrettyOptionsObjectType | any,
    value: string | number
  ) {
    return func(t, null, true)?.[value]
  }

  const objectivesVariables =
    userObjectives && t
      ? {
          contains_goal_infos: true,
          goal_fruit_vegetable: findObjectiveOptionText(
            FRUITS_VEGETABLES_QUANTITY_STEP,
            userObjectives.fruit_and_vegetable
          ),
          goal_starchy: findObjectiveOptionText(
            STARCHY_FOODS_FREQUENCY_STEP,
            userObjectives.complete_carbohydrate
          ),
          goal_dried_vegetable: findObjectiveOptionText(
            DRIED_VEGETABLES_FREQUENCY_STEP,
            userObjectives.dried_vegetable
          ),
          goal_fish: findObjectiveOptionText(
            FISH_FREQUENCY_STEP,
            userObjectives.fish
          ),
          goal_meat: findObjectiveOptionText(
            MEAT_FREQUENCY_STEP,
            userObjectives.meat
          ),
        }
      : {}

  const formVariables = formData
    ? {
        contains_form_infos: true,
        form_name: formData.formName,
        form_has_step: formData.hasStep,
        form_step_number: formData.stepNumber,
        form_step_name: formData.stepName,
        form_is_confirmation: formData.isConfirmation,
      }
    : {}

  const preferencesVariables = userPreferences
    ? {
        contains_preference_infos: true,
        preference_number_people: userPreferences.portions,
        preference_diet: findPreferenceOptionText(
          DIET_PRACTICE_STEP,
          userPreferences.regime as string
        ),
        preference_meal_composition: findPreferenceOptionText(
          COURSE_COMPOSITION_STEP,
          userPreferences.structure as string
        ),
        preference_cook: findPreferenceOptionText(
          EXPRESS_STEP,
          userPreferences?.express ? 'true' : 'false'
        ),
        preference_meal_number: findPreferenceOptionText(
          COURSE_RANGE_STEP,
          userPreferences?.moment || MomentValues.LUNCH_AND_DINNER
        ),
      }
    : {}

  const transformedFilters = filters
    ? {
        'type de plat': filters.courseType.map(
          (item: RecipeCategory) => tcRecipeCategoryFilterValues[item]
        ),
        'régime alimentaire':
          tcRegimeFilterValues[filters.diet as RegimeValues],
        'recette express': filters.cookingTime.length ? 'oui' : 'non',
      }
    : {}

  const tagComanderProps: TcProps = {
    triggerTcUpdate,
    scriptHead: config.tagcommander.scriptHead,
    datalayer: {
      ...sharedTagCommanderVariables,
      env_work: config.tagcommander.envWork,
      page_name: pageName,
      env_template: tcTemplate,
      page_cat1: tcCAt1,
      page_cat2: tcCAt2,
      page_cat3: tcCAt3,
      page_cat4: tcCAt4,
      contains_filter_infos: !!filters,
      ...(filters ? { list_filters: transformedFilters } : {}),
      contains_search_result_pages: !!searchQuery,
      ...(searchQuery ? { search_keyword: searchQuery } : {}),
      ...objectivesVariables,
      ...formVariables,
      ...preferencesVariables,
      ...extraData,
    },
  }

  return tagComanderProps
}
